import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { STAFF_PATH, usePortal } from '../contexts/PortalContext';
import { brand } from '../data/projects';
import { supabase } from '../lib/supabase';
import { completeStaffPasswordReset } from '../utils/staffAuth';
const FIELD = 'mt-4 w-full border-0 border-b border-paper/25 bg-transparent pb-3 pr-10 font-display text-xl text-paper placeholder:text-paper/25 focus:border-paper focus:outline-none';
type State = 'checking' | 'ready' | 'expired' | 'done';

/**
 * The page a staff member lands on from a Supabase password-reset email.
 * Separate from /staff so a recovery link never drops someone on a sign-in
 * form, and never carries them into the dashboard.
 */
export function StaffResetPassword() {
  const {
    navigate
  } = usePortal();
  const [state, setState] = useState<State>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  /* Supabase either raises PASSWORD_RECOVERY as it consumes the link, or has
     already established the recovery session by the time this mounts. */
  useEffect(() => {
    if (!supabase) {
      setState('expired');
      return;
    }
    let settled = false;
    const {
      data
    } = supabase.auth.onAuthStateChange(event => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        settled = true;
        setState('ready');
      }
    });
    void supabase.auth.getSession().then(({
      data: session
    }) => {
      if (settled) return;
      setState(session.session ? 'ready' : 'expired');
    });
    return () => data.subscription.unsubscribe();
  }, []);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await completeStaffPasswordReset(password, confirm);
      if (!result.ok) {
        setError(result.error ?? 'Password could not be updated.');
        return;
      }
      setPassword('');
      setConfirm('');
      setState('done');
    } finally {
      setBusy(false);
    }
  }
  return <div className="flex min-h-screen w-full items-center justify-center bg-ink px-6 py-20 text-paper" data-id="element-859" data-mp="3bczwek2e2ini">
      <div className="w-full max-w-md" data-id="element-860" data-mp="2nnfbhvmodbbl">
        <img src={brand.lockup} alt="Portal Productions" className="h-8 w-auto brightness-0 invert sm:h-10" data-id="element-861" data-mp="2nk9ja7ir1tbn" />

        {state === 'checking' && <p className="label mt-16 text-paper/40" data-id="element-862" data-mp="2xdi4hvtsqp9x">Checking your reset link…</p>}

        {state === 'expired' && <>
            <p className="label mt-16 text-paper/50" data-id="element-863" data-mp="24vmk25n97y5z">Password Reset</p>
            <h1 className="mt-7 whitespace-pre-line font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-864" data-mp="24trz4rbzg61w">
              {'Link no longer\nvalid.'}
            </h1>
            <p className="label-sm mt-10 leading-relaxed text-paper/50" data-id="element-865" data-mp="fd66ip43u7ii">
              This password reset link has expired. Please request a new one.
            </p>
            <button type="button" onClick={() => navigate(STAFF_PATH)} className="label group mt-14 flex w-full items-center justify-between gap-6 border border-paper px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-866" data-mp="5hck8e0l2znj">
              Back to Staff Access
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-867" data-mp="1328t8bdliqjv" />
            </button>
          </>}

        {state === 'done' && <>
            <p className="label mt-16 text-paper/50" data-id="element-868" data-mp="39t82ekdez4cl">Password Reset</p>
            <h1 className="mt-7 font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-869" data-mp="3dwmq7x7lgp0l">
              Password updated
            </h1>
            <p className="label-sm mt-10 leading-relaxed text-paper/50" data-id="element-870" data-mp="3u5lyeu0lslmv">
              You can now sign in with your new password.
            </p>
            <button type="button" onClick={() => navigate(STAFF_PATH)} className="label group mt-14 flex w-full items-center justify-between gap-6 border border-paper px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-871" data-mp="3kcy3k4t6orqs">
              Go to Staff Access
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-872" data-mp="bvtg5ha41see" />
            </button>
          </>}

        {state === 'ready' && <>
            <p className="label mt-16 text-paper/50" data-id="element-873" data-mp="2rqloau10bgxd">Password Reset</p>
            <h1 className="mt-7 whitespace-pre-line font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-874" data-mp="2nn755uhrukfp">
              {'Choose a new\npassword.'}
            </h1>

            <form onSubmit={submit} className="mt-14" data-id="element-875" data-mp="176q9t9ldi3wr">
              <label className="relative block" data-id="element-876" data-mp="1779gtv9hj7w0">
                <span className="label block text-paper/40" data-id="element-877" data-mp="1c5kr273ozker">New password</span>
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" className={FIELD} data-id="element-878" data-mp="1gwt37awepjvm" />
                <button type="button" onClick={() => setShowPassword(current => !current)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} className="absolute bottom-3 right-0 text-paper/40 transition-colors duration-150 ease-out hover:text-paper/80" data-id="element-879" data-mp="1qwtq16thii4k">
                  {showPassword ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-880" data-mp="18rs32zjkknmz" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-881" data-mp="13u8ofyiym8ui" />}
                </button>
              </label>

              <label className="relative mt-10 block" data-id="element-882" data-mp="1huje4y8j9he3">
                <span className="label block text-paper/40" data-id="element-883" data-mp="1ctvpgj7s1cn1">Confirm new password</span>
                <input type={showConfirm ? 'text' : 'password'} value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" className={FIELD} data-id="element-884" data-mp="1rr5v3bxqnoau" />
                <button type="button" onClick={() => setShowConfirm(current => !current)} aria-label={showConfirm ? 'Hide password' : 'Show password'} aria-pressed={showConfirm} className="absolute bottom-3 right-0 text-paper/40 transition-colors duration-150 ease-out hover:text-paper/80" data-id="element-885" data-mp="1wi4e4ja430hs">
                  {showConfirm ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-886" data-mp="2u2nwrrz7h5ka" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-887" data-mp="2f5epfotibr6x" />}
                </button>
              </label>

              {error && <p role="alert" className="label-sm mt-8 leading-relaxed text-paper" data-id="element-888" data-mp="26e4ohcg5oss7">
                  {error}
                </p>}

              <button type="submit" disabled={busy} className="label group mt-14 flex w-full items-center justify-between gap-6 border border-paper px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink disabled:border-paper/25 disabled:text-paper/40 disabled:hover:bg-transparent disabled:hover:text-paper/40" data-id="element-889" data-mp="2b5mnvw2j6lb2">
                Update Password
                <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-890" data-mp="27wczsmspv04i" />
              </button>
            </form>
          </>}

        <p className="label-sm mt-16 max-w-sm leading-relaxed text-paper/25" data-id="element-891" data-mp="1xwm3qgoicd2b">
          Portal team accounts only, by invitation. Client access is provided separately by secure
          project link.
        </p>
      </div>
    </div>;
}